// src/features/october/components/FloatingCartButton.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, X, Plus, Minus, Trash2 } from 'lucide-react';
import { useCart } from '../hooks/useCart';

const FloatingCartButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { cartItems, cartSummary, updateQuantity, removeFromCart, clearCart } = useCart();

  const handleCheckout = () => {
    setIsOpen(false); // Close cart first
    navigate('/checkout'); // Navigate to checkout page
  };

  const buttonVariants = {
    initial: { scale: 0, rotate: -180, opacity: 0 },
    animate: { 
      scale: 1, 
      rotate: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 200, damping: 20 }
    },
    hover: { scale: 1.05, y: -2 }
  };

  const cartPanelVariants = {
    initial: { opacity: 0, scale: 0.95, x: 20 },
    animate: { 
      opacity: 1, 
      scale: 1,
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    exit: { opacity: 0, scale: 0.95, x: 20 }
  };

  return (
    <>
      {/* Minimal Luxury Floating Cart Button - Enhanced Branding */}
      <motion.div
        className="relative z-50"
        variants={buttonVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
      >
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-16 h-16 bg-black/90 backdrop-blur-xl border-2 border-white/20 rounded-3xl shadow-2xl flex items-center justify-center text-white hover:border-red-400/50 hover:shadow-red-500/20 transition-all duration-300"
        >
          <motion.div
            className="relative z-10"
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isOpen ? <X size={22} strokeWidth={1.5} /> : <ShoppingBag size={22} strokeWidth={1.5} />}
          </motion.div>

          {/* Enhanced Cart Count Badge with Balanced Brand Colors */}
          <AnimatePresence>
            {cartSummary.itemCount > 0 && (
              <motion.div
                className="absolute -top-2 -right-2 bg-yellow-500 text-black rounded-full w-7 h-7 flex items-center justify-center text-sm font-medium border-2 border-black/90 shadow-lg"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                whileHover={{ scale: 1.1 }}
              >
                {cartSummary.itemCount}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </motion.div>

      {/* Minimal Cart Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Subtle Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/10 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Clean Cart Panel - Dark Luxury */}
            <motion.div
              className="fixed top-0 right-0 h-full w-full max-w-md bg-black/90 backdrop-blur-xl border-l border-white/10 z-50 overflow-hidden flex flex-col shadow-2xl"
              variants={cartPanelVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {/* Minimal Header - Dark */}
              <div className="flex-shrink-0 p-8 pt-24 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-light text-white tracking-wide">
                    Cart
                  </h2>

                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-3 rounded-2xl text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200"
                  >
                    <X size={20} strokeWidth={1.5} />
                  </button>
                </div>

                <p className="text-gray-400 font-light mt-3 tracking-wide">
                  {cartSummary.itemCount} {cartSummary.itemCount === 1 ? 'item' : 'items'}
                </p>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-8">
                {cartItems.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
                      <ShoppingBag className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-white font-light text-lg mb-3 tracking-wide">Your cart is empty</h3>
                    <p className="text-gray-400 font-light">Start adding some fragrances</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {cartItems.map((item) => (
                      <motion.div
                        key={item.id}
                        className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/10"
                        layout
                        layoutId={`cart-item-${item.id}`}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                      >
                        <div className="flex items-center gap-4">
                          {/* Product Image - Dark */}
                          <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 border border-white/20">
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Product Info - Dark */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-light truncate tracking-wide">{item.name}</h4>
                            <p className="text-gray-400 font-light tracking-wider mt-1">{item.price}</p>
                          </div>

                          {/* Enhanced Quantity Controls with Brand Colors */}
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="w-8 h-8 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/20 transition-all border border-white/20"
                              >
                                <Minus size={14} strokeWidth={1.5} />
                              </button>

                              <span className="text-white font-light w-8 text-center tracking-wide">
                                {item.quantity}
                              </span>

                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="w-8 h-8 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/20 transition-all border border-white/20"
                              >
                                <Plus size={14} strokeWidth={1.5} />
                              </button>
                            </div>

                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="w-8 h-8 bg-yellow-500/20 rounded-xl flex items-center justify-center text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/30 transition-all border border-yellow-500/30"
                            >
                              <Trash2 size={14} strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Enhanced Cart Footer with Brand Colors */}
              {cartItems.length > 0 && (
                <div className="flex-shrink-0 p-8 border-t border-white/10 bg-black/95 backdrop-blur-xl">
                  {/* Clean Total */}
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-gray-400 font-light tracking-wide">Total</span>
                    <span className="text-2xl font-light text-white tracking-wide">
                      {cartSummary.formattedTotal}
                    </span>
                  </div>

                  {/* Enhanced Action Buttons with Balanced Brand Colors */}
                  <div className="space-y-4">
                    <button 
                      onClick={handleCheckout}
                      className="w-full bg-yellow-500 text-black py-4 rounded-2xl font-light tracking-wide flex items-center justify-center gap-3 hover:bg-yellow-600 transition-colors shadow-lg"
                    >
                      <ShoppingBag size={20} strokeWidth={1.5} />
                      Checkout
                    </button>

                    <button
                      onClick={clearCart}
                      className="w-full bg-white/10 backdrop-blur-md text-gray-300 py-4 rounded-2xl font-light tracking-wide hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 transition-all border border-white/20"
                    >
                      Clear Cart
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingCartButton;